import { useState } from "react";
import { CheckCircle2, ExternalLink, Send } from "lucide-react";
import { AUTHOR, CONTACT_LABEL, CONTACT_PROFILE, hasContact } from "../lib/about";
import { sendContactMessage } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import type { UiLang } from "../lib/types";

interface Props {
  lang: UiLang;
  /**
   * Om serveren faktisk kan ta imot en melding.
   *
   * Uten dette sto skjemaet alltid, også når `/api/contact` svarte 503 «ikke satt
   * opp». Besøkende fikk en knapp som så ut som den virket, og en melding som
   * aldri kom fram. Kommentaren under lovte at skjemaet bare sto når serveren var
   * satt opp — det var en beskrivelse av hva som *burde* skjedd.
   */
  contactEnabled?: boolean;
}

/**
 * Hvem som har laget siden, og hvordan man sier fra.
 *
 * ── Hvorfor et skjema og ikke en adresse ───────────────────────────────────
 *
 * Kravet var å kunne motta meldinger uten at e-postadressen står på siden. En
 * `mailto:` ville vært det enkleste og er nettopp det kravet sier nei til: den
 * *er* å publisere adressen, bare skrevet på en annen måte, og adressehøstere
 * leser `mailto:` like godt som brødtekst.
 *
 * Adressen forlater aldri serveren. Den står i en miljøvariabel, avsenderen ser
 * den ikke, og et vellykket svar sier ingenting om hvor meldingen tok veien.
 *
 * ── Hvorfor svaradressen er valgfri ────────────────────────────────────────
 *
 * Fordi et forslag ikke trenger et svar. Å kreve en e-postadresse for å si «her
 * er en rute dere har glemt» ville gjort at de fleste lot være — og den
 * tilbakemeldingen er verdt mer enn muligheten til å svare på den.
 *
 * ── Hva som er av som standard ─────────────────────────────────────────────
 *
 * Skjemaet står bare når serveren er satt opp med nøkkel og mottaker. Er den
 * ikke det, svarer endepunktet 503, og feilteksten sier «ikke satt opp» framfor
 * å se ut som at meldingen forsvant.
 */

/** Samme grense som serveren. Teksten klippes der uansett; her sier vi det. */
const MAX_MESSAGE = 4000;
const MIN_MESSAGE = 10;

type State = "idle" | "sending" | "sent" | "failed";

export function AboutSection({ lang, contactEnabled = false }: Props) {
  const t = dictionaries[lang];
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  /*
   * Honningkrukka. Skjult i skjemaet, så et menneske fyller den aldri ut; en
   * enkel robot fyller ut alt den finner. Serveren kaster meldingen og svarer
   * likevel «sendt» — en feil ville fortalt roboten hva som avslørte den.
   */
  const [company, setCompany] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState("");

  const tooShort = message.trim().length < MIN_MESSAGE;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (tooShort || state === "sending") return;

    setState("sending");
    setError("");
    try {
      await sendContactMessage({ name, replyTo, message, company }, lang);
      setState("sent");
      // Feltene tømmes, så en dobbelttrykk ikke sender det samme igjen.
      setMessage("");
      setName("");
      setReplyTo("");
    } catch (cause) {
      setState("failed");
      setError(cause instanceof Error ? cause.message : t.contactFailed);
    }
  }

  return (
    <section className="card about no-print" id="about">
      <h2 className="card-title">{t.aboutTitle}</h2>
      <p className="about-lede">{t.aboutLede(AUTHOR)}</p>
      <p className="about-body">{t.aboutBody}</p>

      {/*
        Profillenka står ved siden av skjemaet, ikke i stedet for det. De svarer
        på hver sin ting: lenka er «hvem er dette», skjemaet er «si fra».
      */}
      {hasContact() && (
        <a className="about-profile" href={CONTACT_PROFILE} target="_blank" rel="noreferrer">
          {CONTACT_LABEL} <ExternalLink size={12} />
        </a>
      )}

      {/*
        Ingen melding uten et sted å sende den.

        Skjules helt framfor å stå deaktivert: en avslått knapp med en forklaring
        ville vært å be besøkeren om å bry seg om driftstilstanden vår. Profillenka
        over står igjen, og den er en ekte vei fram.
      */}
      {!contactEnabled ? (
        <p className="note">{t.contactOff}</p>
      ) : state === "sent" ? (
        /*
          Kvitteringen erstatter skjemaet framfor å stå over det. Et tomt skjema
          under et «takk» inviterer til å sende det samme en gang til.
        */
        <p className="about-sent">
          <CheckCircle2 size={16} /> {t.contactSent}
        </p>
      ) : (
        <form className="about-form" onSubmit={submit}>
          <label className="field-label" htmlFor="contact-message">
            {t.contactMessageLabel}
          </label>
          <textarea
            id="contact-message"
            className="about-textarea"
            value={message}
            maxLength={MAX_MESSAGE}
            rows={4}
            placeholder={t.contactPlaceholder}
            onChange={(event) => setMessage(event.target.value)}
          />

          <div className="about-row">
            <div className="about-field">
              <label className="field-label" htmlFor="contact-name">
                {t.contactNameLabel}
              </label>
              <input
                id="contact-name"
                className="date-input"
                value={name}
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="about-field">
              <label className="field-label" htmlFor="contact-reply">
                {t.contactReplyLabel}
              </label>
              <input
                id="contact-reply"
                className="date-input"
                type="email"
                value={replyTo}
                maxLength={80}
                onChange={(event) => setReplyTo(event.target.value)}
              />
            </div>
          </div>
          <span className="field-hint">{t.contactReplyHint}</span>

          {/*
            Honningkrukka. `aria-hidden` og `tabIndex={-1}` holder den utenfor
            både skjermlesere og tabuleringen, så den ikke er et felt et
            menneske kan snuble inn i. `autoComplete="off"` er der fordi en
            nettleser ellers kan fylle den ut selv — og da hadde vi avvist en
            ekte melding som robot.
          */}
          <input
            className="about-trap"
            type="text"
            name="company"
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
            value={company}
            onChange={(event) => setCompany(event.target.value)}
          />

          {/* Samme handlingsstil som «lag planen»: rust betyr «dette er handlingen». */}
          <button className="generate about-send" type="submit" disabled={tooShort || state === "sending"}>
            <Send size={14} />
            {state === "sending" ? t.contactSending : t.contactSend}
          </button>

          {/*
            Hvorfor knappen ikke lar seg trykke, som tekst. En deaktivert knapp
            uten forklaring er den samme feilen som `title` på telefon: det står
            ingen steder hva som mangler.
          */}
          {tooShort && message.length > 0 && (
            <p className="note">{t.contactTooShort(MIN_MESSAGE)}</p>
          )}
          {state === "failed" && <p className="note note--warn">{error}</p>}
        </form>
      )}
    </section>
  );
}
